// src/components/project/ProjectUpdate.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import EditorApp from "./EditorApp";
import Config from "../../config/Config";
import { RESOLVE_LOCAL_AUTH_STATE } from "../auth/AuthActions";

async function fetchUsersByIds(ids) {
  if (!ids || ids.length === 0) return [];
  try {
    const results = await Promise.all(
      ids.map((uid) => axios.get(`${Config().userUrl}${uid}/`).then((r) => r.data).catch(() => null))
    );
    return results.filter(Boolean);
  } catch {
    return [];
  }
}

export default function ProjectUpdate() {
  const { id } = useParams();
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);

  const [initialData, setInitialData] = useState(null);
  const [initialContributors, setInitialContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // -------- Load project to prefill --------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get(`${Config().baseUrl}${id}/`, {
          headers: { Authorization: `Token ${auth?.token}` },
        });
        if (cancelled) return;
        const p = res.data || {};
        setInitialData({
          title: p.title || "",
          subtitle: p.subtitle || "",
          body: p.body || "",
          technology: p.technology || "",
          skill: p.skill || "",
          publish: !!p.publish,
        });
        // Load contributor user objects from owner IDs
        const ownerIds = Array.isArray(p.owner) ? p.owner : [];
        const users = await fetchUsersByIds(ownerIds);
        if (!cancelled) setInitialContributors(users);
      } catch (err) {
        console.error('ProjectUpdate Load Error:', err);
        console.error('ProjectUpdate Load Error Response:', err.response);
        console.error('ProjectUpdate Load Error Status:', err.response?.status);
        console.error('ProjectUpdate Load Error Data:', err.response?.data);
        
        if (cancelled) return;
        let errorMessage = "Failed to load project. Please refresh and try again.";
        if (err.response?.status === 404) {
          errorMessage = "Project not found.";
        } else if (err.response?.status === 403) {
          errorMessage = "You don't have permission to edit this project.";
        } else if (err.response?.status === 500) {
          errorMessage = "Server error occurred while loading project.";
        }
        setLoadError(errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, auth?.token]);

  // -------- Save (PATCH) --------
  const onSave = async (payload) => {
    try {
      const res = await axios.patch(`${Config().baseUrl}${id}/`, payload, {
        headers: { Authorization: `Token ${auth?.token}` },
      });
      return { id: res?.data?.id ?? res?.data?.pk ?? id };
    } catch (err) {
      console.error("ProjectUpdate Save Error:", err);
      console.error("ProjectUpdate Save Error Response:", err.response);
      console.error("ProjectUpdate Save Error Status:", err.response?.status);
      console.error("ProjectUpdate Save Error Data:", err.response?.data);
      throw err; // Re-throw so EditorApp can handle the UI error state
    }
  };

  // Stay on page after save
  const onSuccess = () => {
    toast.success("Saved");
  };

  const onCancel = () => {
    window.location = "/";
  };

  if (loading) return null; // or your loader
  if (loadError) {
    return (
      <div className="container" style={{ padding: 16 }}>
        <div className="pc-alert" role="alert">{loadError}</div>
      </div>
    );
  }

  const onDelete = async () => {
    const ok = window.confirm("Delete this project permanently?");
    if (!ok) return;
    try {
      await axios.delete(`${Config().baseUrl}${id}/`, {
        headers: { Authorization: `Token ${auth?.token}` },
      });
      toast.success("Project deleted");
      setTimeout(() => { window.location = "/"; }, 700);
    } catch (err) {
      console.error("Project Delete Error:", err);
      console.error("Project Delete Error Response:", err.response);
      console.error("Project Delete Error Status:", err.response?.status);
      console.error("Project Delete Error Data:", err.response?.data);

      let errorMessage = "Delete failed";
      if (err.response?.status === 403) {
        errorMessage = "You don't have permission to delete this project";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error occurred while deleting project";
      }
      toast.error(errorMessage);
    }
  };

  return (
    <EditorApp
      draftKey={`projectUpdateDraft_${id}`}
      initialData={initialData}
      initialContributors={initialContributors}
      onSave={onSave}
      onSuccess={onSuccess}
      onCancel={onCancel}
      onDelete={onDelete}
      projectId={id}
      previewTheme="light"
      mobileBreakpointPx={768}
    />
  );
}
